import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, retryWhen, tap } from 'rxjs/operators';
import { Mapping } from './dtos/mapping';
import { MappingForm } from './dtos/MappingForm';
import { Rule } from './dtos/rule';
import { RuleForm } from './dtos/ruleForm';
import { genericRetryStrategy } from './retry-strategy';
import { CopyMappingForm } from './dtos/copyMappingForm';

@Injectable({ providedIn: 'root' })
export class MappingService {
  private mappingsUrl = this.baseUrl + '/api/mappings';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string
  ) {}

  public getAll(customerId: string): Observable<Mapping[]> {
    return this.http
      .get<Mapping[]>(this.mappingsUrl + '/GetAll/' + customerId)
      .pipe(retryWhen(genericRetryStrategy()));
  }

  public getMapping(id: string, withValues = false): Observable<Mapping> {
    return this.http
      .get<Mapping>(this.mappingsUrl + '/Get/' + id + '/' + withValues)
      .pipe(catchError(this.handleError<Mapping>('getMapping')));
  }

  public addMapping(mapping: MappingForm): Observable<string> {
    return this.http.post<string>(this.mappingsUrl + '/Add', mapping).pipe(
      tap(() => this.toastr.success('Saved Sucessfully')),
      catchError(this.handleError<string>('AddMapping'))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateMapping(mapping: MappingForm): Observable<any> {
    return this.http
      .put(this.mappingsUrl + '/Update/' + mapping.id, mapping)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<MappingForm>('updateMapping'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public copyMapping(copyMappingForm: CopyMappingForm): Observable<any> {
    return this.http
      .post<string>(this.mappingsUrl + '/Copy', copyMappingForm)
      .pipe(
        tap(() => this.toastr.success('Copied Sucessfully')),
        catchError(this.handleError<CopyMappingForm>('copyMapping'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateMappingValues(mapping: Mapping): Observable<any> {
    return this.http
      .put(this.mappingsUrl + '/UpdateValues/' + mapping.id, mapping)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<Mapping>('updateMappingValues'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete(mappingId: string): Observable<any> {
    return this.http.delete(this.mappingsUrl + '/Delete/' + mappingId).pipe(
      tap(() => this.toastr.success('Deleted sucessfully')),
      catchError(this.handleError<Mapping>('delete mapping'))
    );
  }

  public addRule(rule: RuleForm): Observable<string> {
    return this.http.post<string>(this.mappingsUrl + '/AddRule', rule).pipe(
      tap(() => this.toastr.success('Saved Sucessfully')),
      catchError(this.handleError<string>('AddRule'))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateRule(rule: RuleForm): Observable<any> {
    return this.http
      .put(this.mappingsUrl + '/UpdateRule/' + rule.id, rule)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<RuleForm>('UpdateRule'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteRule(ruleId: string): Observable<any> {
    return this.http
      .delete<Rule>(this.mappingsUrl + '/DeleteRule/' + ruleId)
      .pipe(catchError(this.handleError<Rule>('DeleteRule')));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {
      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
